<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { PageLoading, Tab } from "../../Components/Common/index.svelte";

    import {
    getEncounterStatus,
    getFormatDate,
        getInsuranceType,
        getValidBillingData,
        getYearsOrMonthsOld,
        goToURL,
        historyBack,
        isObjectEmpty,
        replaceURL,
        resetPreventRedirectProperty,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import {
        cacheWorkersCompensation,
        getInsuranceFundById,
    } from "../../Utils/utilCache.svelte";

    import PatientEncounterGeneralInfoTab from "./tabs/PatientEncounterGeneralInfoTab.svelte";
    import PatientEncounterPreopQuestionnaireTab from "./tabs/PatientEncounterPreopQuestionnaireTab.svelte";
    import PatientEncounterBillingTab from "./tabs/PatientEncounterBillingTab.svelte";
    import PatientEncounterCommunicationTab from "./tabs/PatientEncounterCommunicationTab.svelte";
    import PatientEncounterNotesTab from "./tabs/PatientEncounterNotesTab.svelte";
    import PatientEncounterLogsTab from "./tabs/PatientEncounterLogsTab.svelte";

    import {
        workersCompensation,
        mbsItemsLogic,
        asaItemsLogic,
        mbsToAsaDisallowAsaByInsuranceType,
        mbsToAsaDisallowInsuranceTypeByState,
        amaUnitValue,
        noPatientClaimFunds,
        preventRedirect,
        preventRedirectObj,
        EncounterSidePanelProps,
        user
    } from "../../stores";

    import { is_empty } from "svelte/internal";

    import ViewEncounterHeader from "./components/ViewEncounterHeader.svelte";

    import { 
    checkPatientPaidPaymentsViaChangeEncounterStatus,
        editIcon,
        headerRightBtnCls,
    } from "../../Utils/utilConstants.svelte";

    import { isMobile } from "../../stores";
    import CommunicationToggle from "../../Components/Common/CommunicationToggle.svelte";
    import Loader from "../../Components/Common/Loader.svelte";
    import { DirectEncounterStatus, DirectUserRoles, encounterStatus } from "../../Utils/utilEnums.svelte";
    import OcrStickerModal from "../../Components/Common/OcrStickerModal.svelte";
    import NotificationModal from "../../Components/Common/NotificationModal.svelte";
    import CreatePresetModal from "../../Components/Common/CreatePresetModal.svelte";
    import ViewEncounterListModal from "./components/ViewEncounterListModal.svelte";
    import { _getDisplayDate, getOcrText } from "./helpers/encounterHelper.svelte";
    import CustomTooltip from "../../Components/Common/CustomTooltip.svelte";

    export let params,
        isFromCalendarList = false;

    let isLoadingPatientEncounter = false,
        patientEncounter = {},
        patientDetails = {},
        procedureDetails = {},
        doctorDetails = {},
        billingDetails = {},
        patientContacts = [],
        doctorProviders = [],
        quotesData = [], // estimates
        invoicesData = [],
        paymentsData = [], // payments in estimates && payments in invoice
        emailNotifications = [],
        smsNotifications = [];

    let latestQuoteData = {},
        latestQuoteDataItems = [],
        latestInvoiceData = {},
        latestInvoiceDataItems = [],
        mbsTimeData = [];

    // binding data in billing tab - avoid trigger multiple times
    let doctorPresets = [],
        hasDoctorPresetsFetched = false,
        doctorBillingRules = [],
        hasDoctorBillingRulesFetched = false;

    let isUpdatingBillingStatus = false;

    let itemsNotSaved = false;

    let currentTab = "";

    let isEncounterEnableToEditPage = true;

    let isNotesInEdit = false,
        notesRef,
        callbackFunc = () => {};

    let logsLoaded = false, // reset after running opv or claim to trigger encounter logs refresh
        commsLoaded = false,
        isCommsLoading = false,
        eclipseLogsData = [],
        totalEclipseLogs,
        totalActivityLogs,
        extendedLogs,
        activityLogsData = [];

    let hasLoadedEncounterStatusOfPatient = false,
        encounterStatusOfPatient = {};

    let questionnaireAnswer,
        hasQuestionnaireAnswerLoaded = false;
    
    let ocrText = "";

    let headerRef;

    $: if (patientEncounter.updatedByOCRDate)
        ocrText = getOcrText(patientEncounter.updatedByOCRDate);

    $: $preventRedirect = isNotesInEdit;

    $: if ($preventRedirect) updatePreventRedirectObj();
    else resetPreventRedirectProperty();

    $: encounterHaveQuotes = quotesData.length > 0;

    $: encounterHaveInvoices = invoicesData.length > 0;

    $: showQuestionnaireSent =
        patientEncounter && patientEncounter.hasPreOpQuestionnaireSent;
    // checkIfScheduledDateMoreThan3Days(patientEncounter);

    $: showSurveySent =
        patientEncounter && patientEncounter.hasPostOpSurveySent;

    $: if (currentTab === "communication")
        loadCommunicationData();

    $: isUserEnableUpdatePatientEncounterStatus =
        $user.role >= 2 && $user.role != DirectUserRoles.Developer;

    onMount(async () => {
        if (params.id) await loadPatientEncounter(params.id);

        if (!params.query) currentTab = "generalInfo";
        else _syncTab(params.query);
    });

    const updateURL = () => {
        if (isFromCalendarList) return;

        let url = `/PatientEncounter/view/${params.id}/`;

        if (currentTab) url += `tab=${currentTab}`;

        replaceURL(url);
    };

    const loadPatientEncounter = async (id) => {
        isLoadingPatientEncounter = true;

        await apiMethods.getMethod
            .getPatientEncounterById(id, true)
            .then(async (res) => {
                if (res.error) showError(res.error, historyBack);
                else {
                    patientEncounter = { ...res };

                    await _updatePatientDetails();

                    if (
                        !$mbsItemsLogic.length &&
                        !$mbsToAsaDisallowAsaByInsuranceType.length &&
                        isObjectEmpty($mbsToAsaDisallowInsuranceTypeByState)
                    )
                        await loadItemsLogic();

                    _updateProcedureDetails();

                    _updateDoctorDetails();

                    _updateBillingDetails();

                    _updateQuotesData();

                    _updateInvoicesData();

                    _updatePaymentsData();

                    await _syncAsaTimeData();
                }
            })
            .catch((err) => showException(err, historyBack));

        // doctor providers in this case shall NEVER be changed while investigating encounter
        await apiMethods.getMethod
            .getAccountProvider(doctorDetails.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else doctorProviders = [...res.results];
            })
            .catch((err) => showException(err));

        isEncounterEnableToEditPage = _checkEncounterEnableToEditPage();

        isLoadingPatientEncounter = false;
    };

    const fullEncounterReload = () => {
        if (params.id) loadPatientEncounter(params.id);

        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;
    };

    const reloadPatientEncounter = async (response = {}) => {
        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;

        if (response && !isObjectEmpty(response)) {
            patientEncounter = { ...response };
            return;
        }

        await apiMethods.getMethod
            .getPatientEncounterById(params.id, true)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to reload this encounter details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else patientEncounter = { ...res };
            })
            .catch((err) => showError(err));
    };

    const loadItemsLogic = async () => {
        await apiMethods.getMethod
            .getItemsLogic()
            .then((res) => {
                if (!res || res.error) showError("Failed to load item logic");
                else _handleItemsLogic(res);
            })
            .catch((err) => showException(err));
    };

    const reloadPatientDetails = async () => {
        await reloadPatientEncounter();

        await _updatePatientDetails(true);

        isEncounterEnableToEditPage = _checkEncounterEnableToEditPage();

        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;
    };

    const reloadDoctorDetails = async (id) => {
        await reloadPatientEncounter();

        _updateDoctorDetails();

        isEncounterEnableToEditPage = _checkEncounterEnableToEditPage();

        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;
    };

    const reloadProcedureDetails = async () => {
        await reloadPatientEncounter();

        _updateProcedureDetails();

        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;
    };

    const reloadCommunication = async () => {
        commsLoaded = false;
        setTimeout(() => {
            isEncounterEnableToEditPage = _checkEncounterEnableToEditPage();
        }, 500);

        await loadCommunicationData();

        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;
    };

    const updatePatientEncounter = (billingDataArr = []) => {
        if (billingDataArr.length) {
            patientEncounter = {
                ...patientEncounter,
                billingData: [...billingDataArr],
            };

            apiMethods.putMethod
                .updatePatientEncounter(patientEncounter, false, "Update encounter")
                .then(async (res) => {
                    if (!res.error) await reloadPatientEncounter(res);
                    else showError(res.error);
                });
        } else reloadPatientDetails();
    };

    const reloadBillingDetails = async (
        updateCommunication = false,
        response = {},
    ) => {

        if (!params.id) return;

        await reloadPatientEncounter(response);

        paymentsData = [];

        await _updatePatientDetails();

        _updateBillingDetails();

        _updateQuotesData();

        _updateInvoicesData();

        _updatePaymentsData();

        paymentsData = [...paymentsData];

        if (updateCommunication) await loadCommunicationData();

        await _syncAsaTimeData();

        isEncounterEnableToEditPage = _checkEncounterEnableToEditPage();

        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;

        if(headerRef?.loadHeader) {
            headerRef.loadHeader();
        }
    };

    const updatePreventRedirectObj = () => {
        $preventRedirectObj = {
            message:
                "Your changes have not been saved, do you want to save before leaving this page?",
            btnOptions: [
                {
                    class: "btn-error",
                    text: "Discard changes",
                    function: () => discardChangeAndRedirect(callbackFunc),
                },
                {
                    text: "Save and continue",
                    function: () => confirmChangeAndRedirect(callbackFunc),
                },
            ],
        };
    };

    const discardChangeAndRedirect = async (callback) => {
        await notesRef.discardChanges();

        callback();
    };

    const confirmChangeAndRedirect = async (callback) => {
        await notesRef.updateNotes();

        callback();
    };

    const loadEncounterStatusOfPatient = async () => {
        if (hasLoadedEncounterStatusOfPatient) return encounterStatusOfPatient;
        //load encounters
        await apiMethods.getMethod
            .getPatientEncounter(0, 999999, {
                "filter[patientId]": patientDetails.id,
                "filter[status]": Object.values(DirectEncounterStatus),
                sort: "scheduleddatedesc",
            })
            .then((res) => {
                if (!res || res.error)
                    encounterStatusOfPatient.error =
                        res.error ??
                        "Failed to load encounter status of patient, please refresh the page and try again and contact support if the issue persists.";
                else {
                    hasLoadedEncounterStatusOfPatient = true;

                    const { total, results } = res;

                    encounterStatusOfPatient.total = total;
                    encounterStatusOfPatient.results = results;

                    let stillOpen = Number(0),
                        archived = Number(0),
                        onHold = Number(0);

                    results.map((r) => {
                        const { status, billingStatus } = r;

                        if (billingStatus < 5 && status != 9) stillOpen++;

                        if (billingStatus < 5 && status == 1) onHold++;

                        if (status == 9) archived++;
                    });

                    encounterStatusOfPatient.stillOpen = stillOpen;
                    encounterStatusOfPatient.onHold = onHold;
                    encounterStatusOfPatient.archived = archived;

                }
            });
        
        //load fee estimates
        await apiMethods.getMethod
            .getPatientEncounter(0, 999999, {
                "filter[patientId]": patientDetails.id,
                "filter[quoteOnly]": 1,
                "filter[status]": Object.values(DirectEncounterStatus),
                sort: "scheduleddatedesc",
            })
            .then((res) => {
                if (!res || res.error)
                    encounterStatusOfPatient.error =
                        res.error ??
                        "Failed to load fee estimate status of patient, please refresh the page and try again and contact support if the issue persists.";
                else {
                    hasLoadedEncounterStatusOfPatient = true;

                    const { total, results } = res;

                    encounterStatusOfPatient.totalFeeEstimates = total;
                    encounterStatusOfPatient.feeEstimatesResults = results;
                }
            });


        return encounterStatusOfPatient;
    };

    // HELPER FUNCTIONS
    const _syncTab = (query) => {
        const arr = query.split("&");

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].includes("tab=")) currentTab = arr[i].split("=")[1];
        }

        if (
            currentTab == "preOpQuestionnaire" &&
            !patientEncounter.preOpQuestionnaireId
        )
            currentTab = "generalInfo";

        updateURL();
    };

    const _updatePatientDetails = async (reload = false) => {
        const {
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
        } = patientEncounter;

        const latestBillingData = getValidBillingData(
            patientEncounter.billingData,
        );

        patientDetails = {
            ...patientEncounter.patient,
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
            patientEncounterId: patientEncounter.id,
        };

        patientContacts = [];

        patientContacts = patientDetails.patientContacts;

        if (!isObjectEmpty(latestBillingData)) {
            const {
                patientInsurance,
                patientInsuranceId,
                patientInsuranceType,
                patientMedicareNo,
                patientMedicareRefNo,
                patientMembershipNo,
                patientMembershipRefNo,
                patientVeteranNo,
            } = latestBillingData;

            patientDetails = {
                ...patientDetails,
                insurance: patientInsurance.replaceAll("undefined", ""),
                insuranceId: patientInsuranceId,
                insuranceType: patientInsuranceType,
                medicareNo: patientMedicareNo,
                medicareRefNo: patientMedicareRefNo,
                membershipNo: patientMembershipNo,
                membershipRefNo: patientMembershipRefNo,
                veteranNo: patientVeteranNo,
            };
        }

        if (patientDetails.insuranceType == 16) {
            if (
                patientEncounter.patientEncounterWorkCoverData &&
                !isObjectEmpty(patientEncounter.patientEncounterWorkCoverData)
            ) {
                patientDetails.insuranceName = getInsuranceType(
                    patientDetails.insuranceType,
                );

                await cacheWorkersCompensation();

                patientDetails.workcoverData = $workersCompensation.find(
                    (wc) =>
                        wc.id ==
                        patientEncounter.patientEncounterWorkCoverData
                            .workCoverAuthority,
                );
            }
        } else if (
            (patientDetails.insuranceType == 0 || patientDetails.insuranceType == 10) &&
            patientDetails.insuranceId
        ) {
            let res = await getInsuranceFundById(patientDetails.insuranceId);

            patientDetails.insuranceName = res?.name ?? "";
            patientDetails.showFundCoverType = res?.id
                ? res.eclipseCode.toUpperCase() === "AMA" ||
                  res.eclipseCode.toUpperCase() === "AUH"
                : false;
        } else if(patientDetails.insuranceType == 10 && patientDetails.insuranceId == 0) {
                //default overseas fund
                patientDetails.insuranceName = "Other Overseas Insurance";
        } 
        else {
             patientDetails.insuranceName = getInsuranceType(
                patientDetails.insuranceType,
            );
        }
        
           

        // DVA insurance shows veteranNo instead of insurance
        if (patientDetails.insuranceType == 20)
            patientDetails.insurance = patientDetails.veteranNo;
    };

    const _updateProcedureDetails = () => {
        const {
            procedureName,
            scheduledDate,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
            id: encounterId,
        } = patientEncounter;

        procedureDetails = {
            procedureName,
            scheduledDate,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
            encounterId,
        };

        if (patientEncounter.hospital?.id) {
            procedureDetails = {
                ...procedureDetails,
                hospital: { ...patientEncounter.hospital },
            };
        } else procedureDetails.hospital = {};

        if (patientEncounter.surgeon?.id) {
            procedureDetails = {
                ...procedureDetails,
                surgeon: { ...patientEncounter.surgeon },
            };
        } else procedureDetails.surgeon = {};
    };

    const _updateDoctorDetails = () => {
        const {
            doctor: {
                firstName,
                lastName,
                fullName,
                title,
                phone,
                id,
                profile,
            },
        } = patientEncounter;

        doctorDetails = {
            firstName,
            lastName,
            fullName,
            title,
            phone,
            id,
            profile,
        };

        if (patientEncounter.doctorProvider) {
            const {
                doctorProvider: {
                    number,
                    location,
                    locationAddress,
                    locationAddress2,
                    city,
                    state,
                    postCode,
                    isNoGapBupa,
                    isNoGapHCF,
                    isNoGapNib,
                    isPreferredDefence,
                },
            } = patientEncounter;

            doctorDetails = {
                ...doctorDetails,
                number,
                location,
                locationAddress,
                locationAddress2,
                city,
                state,
                postCode,
                isNoGapBupa,
                isNoGapHCF,
                isNoGapNib,
                isPreferredDefence,
            };
        }
    };

    const _updateBillingDetails = async () => {
        isUpdatingBillingStatus = true;

        const {
            billingStatus,
            skipQuote,
            eligibilityStatusMedicare,
            eligibilityStatusHealthFund,
            eligibilityManuallyApproved,
            claimStatus,
            claimERAStatus,
            claimTransactionId,
            claimLastError,
            claimLastTry,
            claimTries,
            patientEncounterWorkCoverData,
            patientEncounterPIPData,
            handOver,
            doctor: { handOver: showHandOverDropdown },
            patientClaim,
        } = patientEncounter;

        billingDetails = {
            billingStatus,
            defaultBillingStatus: billingStatus,
            skipQuote,
            eligibilityStatusMedicare,
            eligibilityStatusHealthFund,
            eligibilityManuallyApproved,
            claimStatus,
            claimERAStatus,
            claimTransactionId,
            patientEncounterId: patientEncounter.id,
            claimLastError,
            claimLastTry,
            claimTries,
            patientEncounterWorkCoverData,
            patientEncounterPIPData,
            handOver,
            showHandOverDropdown,
            patientClaim,
        };

        if (
            patientEncounter.billingData &&
            patientEncounter.billingData.length
        ) {
            billingDetails.billingData = getValidBillingData(
                patientEncounter.billingData,
            );

            billingDetails.billingType =
                getValidBillingData(patientEncounter.billingData).billingType ??
                "";
        } else billingDetails.billingData = {};

        await _updateLatestDetails();

        isUpdatingBillingStatus = false;
    };

    const _updateLatestDetails = async () => {
        latestQuoteData = patientEncounter.billingData?.length
            ? (patientEncounter.billingData.filter(
                  (bd) => bd.isLatestQuoteData,
              )[0] ?? {})
            : {};

        latestInvoiceData = patientEncounter.billingData?.length
            ? (patientEncounter.billingData.filter(
                  (bd) => bd.isLatestInvoiceData,
              )[0] ?? {})
            : {};

        latestQuoteDataItems = !isObjectEmpty(latestQuoteData)
            ? latestQuoteData.billingCodesType == 0
                ? latestQuoteData.mbsData?.length
                    ? [...latestQuoteData.mbsData]
                    : []
                : latestQuoteData.billingCodesType == 1
                  ? latestQuoteData.asaData?.length
                      ? [...latestQuoteData.asaData]
                      : []
                  : latestQuoteData.billingCodesType == 2
                    ? latestQuoteData.freeTextData?.length
                        ? [...latestQuoteData.freeTextData]
                        : []
                    : []
            : [];

        latestInvoiceDataItems = !isObjectEmpty(latestInvoiceData)
            ? latestInvoiceData.billingCodesType == 0
                ? latestInvoiceData.mbsData?.length
                    ? [...latestInvoiceData.mbsData]
                    : []
                : latestInvoiceData.billingCodesType == 1
                  ? latestInvoiceData.asaData?.length
                      ? [...latestInvoiceData.asaData]
                      : []
                  : latestInvoiceData.billingCodesType == 2
                    ? latestInvoiceData.freeTextData?.length
                        ? [...latestInvoiceData.freeTextData]
                        : []
                    : []
            : [];
    };

    const _updateQuotesData = () => {
        quotesData = [];

        if (!patientEncounter.estimates.length) return;

        quotesData = [...patientEncounter.estimates.sort((a, b) =>
            a.status === 9 && b.status !== 9 ? 1 : 0,
        )];
        // show status 9 - cancelled to the end
    };

    const _updateInvoicesData = () => {
        invoicesData = [];

        if (!patientEncounter.invoices.length) return;

        invoicesData = [...patientEncounter.invoices.sort((a, b) =>
            a.status === 9 && b.status !== 9 ? 1 : 0,
        )];
        // show status 9 - cancelled to the end
    };

    const _updatePaymentsData = () => {
        paymentsData = [];
        if (quotesData.length)
            quotesData.map((quote) => {
                const { estimateNumber, patientEncounterBillingDataId } = quote;
                if (quote.payments.length)
                    quote.payments.forEach((payment) => {
                        // Currently show all payments regardless of their billing entity status
                        // if (
                        //     quote.status === 9 &&
                        //     hiddenPaymentStatusIDsWithinCancelledParent.includes(
                        //         payment.status,
                        //     )
                        // )
                        //     return;
                        paymentsData.push({
                            ...payment,
                            estimateNumber,
                            patientEncounterBillingDataId,
                        });
                    });
            });

        if (invoicesData.length)
            invoicesData.map((invoice) => {
                const { invoiceNumber, patientEncounterBillingDataId } =
                    invoice;
                if (invoice.payments.length)
                    invoice.payments.map((payment) => {
                        // Currently show all payments regardless of their billing entity status
                        // if (
                        //     invoice.status === 9 &&
                        //     hiddenPaymentStatusIDsWithinCancelledParent.includes(
                        //         payment.status,
                        //     )
                        // )
                        //     return;
                        paymentsData.push({
                            ...payment,
                            invoiceNumber,
                            patientEncounterBillingDataId,
                        });
                    });
            });
    };

    const loadCommunicationData = async () => {
        isCommsLoading = true;
        await apiMethods.getMethod
            .getEncounterCommunications(params.id)
            .then((res) => {
                isCommsLoading = false;
                if (res.error) showError(res.error);
                else {
                    emailNotifications = res.emailNotifications;
                    smsNotifications = res.smsNotifications;
                    commsLoaded = true;
                }
            })
            .catch((err) => {
                isCommsLoading = false;
                showException(err);
            });
    };

    const _syncAsaTimeData = async () => {
        const latestQuoteDataHasAsaTimeData =
            // latestQuoteDataItems.length &&
            latestQuoteData.useAsaCodes &&
            latestQuoteData.asaTimeData &&
            latestQuoteData.asaTimeData.length;

        const latestInvoiceDataHasAsaTimeData =
            // latestInvoiceDataItems.length &&
            latestInvoiceData.useAsaCodes &&
            latestInvoiceData.asaTimeData &&
            latestInvoiceData.asaTimeData.length;

        if (
            (latestQuoteDataHasAsaTimeData ||
                latestInvoiceDataHasAsaTimeData) &&
            !mbsTimeData.length
        )
            await apiMethods.getMethod
                .getMBSItems(0, 999999, { "filter[infoType]": "Time" })
                .then((res) => {
                    if (res.results?.length) mbsTimeData = [...res.results];
                });

        if (latestQuoteDataHasAsaTimeData) {
            const targetItem = mbsTimeData.find(
                (td) => td.basicUnits == latestQuoteData.asaTimeData[0].units,
            );

            latestQuoteDataItems = [
                ...latestQuoteDataItems,
                {
                    ...latestQuoteData.asaTimeData[0],
                    amaUnits: latestQuoteData.asaTimeData[0].units,
                    rebate: latestQuoteData.asaTimeData[0].feeCharged,
                    rebateAMA: latestQuoteData.asaTimeData[0].feeCharged,
                    mbsItemNum: targetItem?.itemNum,
                },
            ];
        }

        if (latestInvoiceDataHasAsaTimeData) {
            const targetItem = mbsTimeData.find(
                (td) => td.basicUnits == latestInvoiceData.asaTimeData[0].units,
            );

            latestInvoiceDataItems = [
                ...latestInvoiceDataItems,
                {
                    ...latestInvoiceData.asaTimeData[0],
                    amaUnits: latestInvoiceData.asaTimeData[0].units,
                    rebate: latestInvoiceData.asaTimeData[0].feeCharged,
                    rebateAMA: latestInvoiceData.asaTimeData[0].feeCharged,
                    mbsItemNum: targetItem?.itemNum,
                },
            ];
        }
    };

    const _checkItemsNotSaved = (tab = "") => {
        if (isNotesInEdit) {
            $preventRedirectObj.showModal = $preventRedirect;

            callbackFunc = () => _handleSwitchTab(tab);
        } else {
            callbackFunc = () => {};
            if (!tab) return;
            _handleSwitchTab(tab);
        }
    };

    const _handleSwitchTab = (tab = "") => {
        if (!tab) return;

        if (
            (tab == "preOpQuestionnaire" &&
                !patientEncounter.preOpQuestionnaireId) ||
            (tab == "postOpSurvey" && !patientEncounter.postOpSurveyId)
        )
            currentTab = "generalInfo";
        else currentTab = tab;

        updateURL();
    };

    const _handleItemsLogic = ({
        AsaLogic,
        MbsLogic,
        MbsToAsa,
        InsuranceRulesLogic,
    }) => {
        _syncItemsLogic("mbs", MbsLogic.ConditionalItemsHide);
        _syncItemsLogic("asa", AsaLogic.ConditionalItemsHide);
        _syncItemsLogic("insuranceType", MbsToAsa.DisallowAsaByInsuranceType);
        _syncItemsLogic(
            "insuranceTypeByState",
            MbsToAsa.DisallowSwitchMbsToAsaInInvoiceIfQuoteIsMbsByInsuranceTypeAndState,
        );
        _syncItemsLogic(
            "insuranceRulesLogic",
            InsuranceRulesLogic.AMAUnitValue,
        );
        _syncItemsLogic(
            "noPatientClaimFunds",
            InsuranceRulesLogic.NoPatientClaimFunds
        );
    };

    const editEncounter = () => goToURL(`/PatientEncounter/edit/${params.id}`);

    const _syncItemsLogic = (type, conditionalObj = {}) => {
        if (type == "insuranceRulesLogic")
            $amaUnitValue = Number(conditionalObj);

        if (type == "noPatientClaimFunds")
            $noPatientClaimFunds = conditionalObj;

        if (isObjectEmpty(conditionalObj)) return;

        if (type == "insuranceType")
            $mbsToAsaDisallowAsaByInsuranceType = [...conditionalObj];
        else {
            const keys = Object.keys(conditionalObj);

            for (const key of keys) {
                switch (type) {
                    case "mbs":
                        $mbsItemsLogic.push({
                            hideItem: key,
                            targetItem: conditionalObj[key],
                        });
                        break;
                    case "asa":
                        $asaItemsLogic.push({
                            hideItem: key,
                            targetItem: conditionalObj[key],
                        });
                        break;
                    case "insuranceTypeByState":
                        $mbsToAsaDisallowInsuranceTypeByState = {
                            ...conditionalObj,
                        };
                        break;
                    default:
                        break;
                }
            }
        }
    };

    const _getPageTitle = (encounter, patient, procedure) => {
        let followingString = "",
            encounterIdString = "";
        if (!is_empty(patient)) {
            if (patient.titleName && patient.titleName !== "Other")
                followingString += `${patient.titleName}`;

            if (patient.firstName) followingString += ` ${patient.firstName}`;

            if (patient.lastName) followingString += ` ${patient.lastName}`;

            if (patient.sexName && patient.sexName !== "NotSpecified")
                followingString += `, ${patient.sexName}`;

            if (patient.birthDate)
                followingString += `, ${getYearsOrMonthsOld(
                    patient.birthDate,
                )}`;
        }
        if (!is_empty(procedure) && procedure.procedureName)
            followingString += `, ${procedure.procedureName.substring(0, 32)}`;

        if (followingString.length) followingString = " - " + followingString;

        if (!is_empty(encounter)) encounterIdString = " " + encounter.id;

        return `View Encounter${encounterIdString}${followingString}`;
    };

    const _checkEncounterEnableToEditPage = () => {
        const { isDeleted } = patientEncounter;

        if (isDeleted) return false;

        return true;
    };

    const checkIfScheduledDateMoreThan3Days = (encounter) => {
        if (encounter && encounter.scheduledDate) {
            const now = new Date();
            const threeDaysLater = new Date(now.setDate(now.getDate() + 3));
            const scheduledDate = new Date(encounter.scheduledDate);
            return scheduledDate > threeDaysLater;
        }
        return false;
    };

    /*Buttons*/

    let isChangingStatusOptionsOpen = false;
    let isUpdatingEncounterStatus = false;

    const _showStatusMenu = () => {
        if (document.getElementById("encounterStatusDropDownMenu")) {
            document.getElementById(
                "encounterStatusDropDownMenu",
            ).style.display = "flex";
            isChangingStatusOptionsOpen = true;
        }
    };

    const _hideStatusMenu = () => {
        if (document.getElementById("encounterStatusDropDownMenu")) {
            document.getElementById(
                "encounterStatusDropDownMenu",
            ).style.display = "none";
            isChangingStatusOptionsOpen = false;
        }
    };

    const _getStatusBackgroundColor = (status) => {
        if (status == 9) return "bg-error";
        if (status == 0) return "";
        return "bg-warning";
    };

    const handleUpdateStatus = async (addSkipRefundToUrl = false) => {
        if (isUpdatingEncounterStatus) return;

        isUpdatingEncounterStatus = true;

        if (checkClaimStatusViaChangeStatus.includes(newStatus)) {
            if (encounterHasActiveClaim) {
                encounterHasActiveClaimStatus &&
                    (await apiMethods.putMethod.updateClaimStatus(
                        patientEncounter.id,
                        10,
                    ));
                encounterHasActiveClaimEraStatus &&
                    (await apiMethods.putMethod.updateClaimEraStatus(
                        patientEncounter.id,
                        10,
                    ));
            }

            if (
                checkPatientPaidPaymentsViaChangeEncounterStatus.includes(
                    newStatus,
                ) &&
                encounterHasPatientPaidPayments &&
                addSkipRefundToUrl
            )
                await cancelEncounterWithPatientPayment();
            else await updateEncounterStatus(newStatus);
        } else if (newStatus == 0 && patientEncounter.newStatus == 9)
            // restore deleted encounter back to active status
            await confirmUnArchivePatientEncounter();
        else await updateEncounterStatus(newStatus);

        isUpdatingEncounterStatus = false;

        _closeConfirmUpdateEncounterStatusModal();

        // reload calendar info after close side panel
        if (isFromCalendarList)
            $EncounterSidePanelProps.shouldTriggerCloseFunction = true;
    };

     const cancelEncounterWithPatientPayment = async () => {
        await apiMethods.putMethod
            .cancelEncounterWithoutRefund(patientEncounter.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to cancel encounter, please refresh the page and try again and contact support if the issue persists.",
                    );
                else reloadPatientEncounter();
            });
    };

    const confirmUnArchivePatientEncounter = async () => {
        await apiMethods.putMethod
            .unDeletePatientEncounter(patientEncounter.id)
            .then(async (res) => {
                if (res.id) await reloadPatientEncounter();
                else
                    showError(
                        `Failed to un-archive this patient encounter, please refresh the page and try again and contact support if the issue persists. \n${res.error}`,
                    );
            })
            .catch((err) =>
                showException(
                    `Failed to un-archive this patient encounter, please refresh the page and try again and contact support if the issue persists. \n${err}`,
                ),
            );
    };

    const updateEncounterStatus = async (status) => {
        if (status != 9)
            await apiMethods.putMethod
                .updateEncounterStatus(patientEncounter.id, status)
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to update encounter status, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else reloadPatientEncounter();
                });
        else await confirmDeletePatientEncounter();
    };

    const confirmDeletePatientEncounter = async () => {
        await apiMethods.deleteMethod
            .deletePatientEncounter(patientEncounter.id)
            .then(async (res) => {
                if (res.id) await reloadPatientEncounter();
                else if (
                    res.error &&
                    res.error.includes("Encounter has a claim")
                ) {
                    showError(
                        "For purposes of auditing, we are unable to archive encounters with an active claim.",
                        undefined,
                        true,
                    );
                } else {
                    showError(
                        `Failed to archive this patient encounter, please try again later or contact for support...\n${res.error}`,
                        undefined,
                        true,
                    );
                }
            })
            .catch((err) =>
                showException(
                    `Failed to archive this patient encounter, please try again later or contact for support...\n${err}`,
                    undefined,
                    true,
                ),
            );
    };

    const _openConfirmUpdateEncounterStatusModal = (status) => {
        _hideStatusMenu();
        newStatus = status;

        _determineArchiveWithActiveClaim();

        _determineCancelWithPatientPaidPayments();

        if (
            encounterHasPatientPaidPayments &&
            checkPatientPaidPaymentsViaChangeEncounterStatus.includes(newStatus)
        )
            isConfirmCancelEncounterWithPatientPaidPaymentsModalOpen = true;
        else isConfirmUpdateEncounterStatusModalOpen = true;
    };

    let isOcrStickerModalOpen = false,
        isEncounterListModalOpen = false;

    let isSavePresetModalOpen = false,
        isSuccessSavePresetModalOpen = false;

    let isConfirmUpdateEncounterStatusModalOpen = false,
        newStatus = null;
    
    $: encounterEnableSaveAsPreset =
        patientEncounter.billingStatus == 5 && !patientEncounter.quoteOnly;

    $: isPatientQuote = patientEncounter.quoteOnly;

    const showOcrStickerModal = () => (isOcrStickerModalOpen = true);

    const showSavePresetModal = () => (isSavePresetModalOpen = true);
    
    const duplicateEncounter = () => {
        $preventRedirectObj.showModal = $preventRedirect;

        if (isNotesInEdit)
            callbackFunc = () =>
                goToURL(
                    `/${isPatientQuote ? "PatientQuote" : "PatientEncounter"}/edit/${patientEncounter.id}/isDuplicate=2`,
                );
        else if (isFromCalendarList)
            $EncounterSidePanelProps.type = "duplicate";
        else
            goToURL(
                `/${isPatientQuote ? "PatientQuote" : "PatientEncounter"}/edit/${patientEncounter.id}/isDuplicate=2`,
            );
    };

    $: encounterDoctorId = patientEncounter.doctor?.id;

    const openEncounterListModal = () => {
        if (encounterDoctorId && patientEncounter.id)
            isEncounterListModalOpen = true;
    };

    const closeSavePresetModal = (showCompleteModal = false) => {
        isSavePresetModalOpen = false;
        isSuccessSavePresetModalOpen = showCompleteModal;
    };

    const _closeConfirmUpdateEncounterStatusModal = () => {
        newStatus = null;
        isConfirmUpdateEncounterStatusModalOpen = false;
        isConfirmCancelEncounterWithPatientPaidPaymentsModalOpen = false;
    };

    let encounterHasActiveClaim = false,
        encounterHasActiveClaimStatus = false,
        encounterHasActiveClaimEraStatus = false;

    let encounterHasPatientPaidPayments = false,
        isConfirmCancelEncounterWithPatientPaidPaymentsModalOpen = false;

    const checkClaimStatusViaChangeStatus = [7, 8, 9];

    const _determineArchiveWithActiveClaim = () => {
        const { claimERAStatus, claimStatus, claimTransactionId } =
            patientEncounter;

        encounterHasActiveClaimStatus = ![5, 10].includes(claimStatus);
        encounterHasActiveClaimEraStatus = ![2, 10].includes(claimERAStatus);

        encounterHasActiveClaim = checkClaimStatusViaChangeStatus.includes(
            newStatus,
        )
            ? !!claimTransactionId &&
              (encounterHasActiveClaimStatus ||
                  encounterHasActiveClaimEraStatus)
            : false;
    };

    const _determineCancelWithPatientPaidPayments = () => {
        if (!paymentsData) encounterHasPatientPaidPayments = false;
        else
            encounterHasPatientPaidPayments = paymentsData.some(
                ({ paidBy, status }) => paidBy == 0 && [1, 2].includes(status),
            );
    };

</script>

{#if isLoadingPatientEncounter}
    <PageLoading />
{:else}
    <ViewEncounterHeader
        bind:this={headerRef}
        bind:patientEncounter={patientEncounter} 
        {patientDetails}
        {procedureDetails}
        {paymentsData}
        {isLoadingPatientEncounter}
        {isNotesInEdit}
        bind:callbackFunc
        reloadPatientEncounter={reloadBillingDetails}
        {isFromCalendarList}
    />

    <div class="flex flex-1 flex-col shadow-2xl rounded-lg bg-white">
        <div class="flex flex-row justify-between gap-x-2 ml-4 mt-4 mr-4">
            <div class="flex flex-1 items-end flex-wrap gap-y-2">
                <Tab
                    {currentTab}
                    displayTab="General Info"
                    tabUrl="generalInfo"
                    clickTabFunc={() => _checkItemsNotSaved("generalInfo")}
                />

                <Tab
                    {currentTab}
                    displayTab="PreOp Questionnaire"
                    tabUrl="preOpQuestionnaire"
                    clickTabFunc={() =>
                        _checkItemsNotSaved("preOpQuestionnaire")}
                    isHidden={!patientEncounter.preOpQuestionnaireId}
                >
                    {#if patientEncounter.hasPreOpQuestionnaireAnswers}
                        <div
                            class="tooltip tooltip-secondary ml-3"
                            data-tip="Questionnaire submitted and ready for review"
                        >
                            <i
                                class="fa fa-check text-success"
                                aria-hidden="true"
                            ></i>
                        </div>
                    {:else if showQuestionnaireSent}
                        <div
                            class="tooltip tooltip-secondary ml-3"
                            data-tip="Questionnaire sent"
                        >
                            <i class="fa fa-paper-plane" aria-hidden="true"></i>
                        </div>
                    {/if}
                </Tab>

                <!-- <Tab
                    {currentTab}
                    displayTab="PostOp Survey"
                    tabUrl="postOpSurvey"
                    clickTabFunc={() => _checkItemsNotSaved("postOpSurvey")}
                    isHidden={!patientEncounter.postOpSurveyId}
                >
                    {#if patientEncounter.hasPostOpSurveyAnswers}
                        <div
                            class="tooltip tooltip-secondary ml-3"
                            data-tip="Survey submitted and ready for review"
                        >
                            <i
                                class="fa fa-check text-success"
                                aria-hidden="true"
                            ></i>
                        </div>
                    {:else if showSurveySent}
                        <div
                            class="tooltip tooltip-secondary ml-3"
                            data-tip="Survey sent"
                        >
                            <i class="fa fa-paper-plane" aria-hidden="true"></i>
                        </div>
                    {/if}
                </Tab> -->

                <Tab
                    {currentTab}
                    displayTab="Billing"
                    tabUrl="billing"
                    clickTabFunc={() => _checkItemsNotSaved("billing")}
                />

                <Tab
                    {currentTab}
                    displayTab="Notes"
                    tabUrl="notes"
                    clickTabFunc={() => _checkItemsNotSaved("notes")}
                >
                    {#if patientEncounter.hasNotes || patientDetails?.hasNotes}
                        <div
                            class="tooltip tooltip-secondary ml-3"
                            data-tip="Notes available"
                        >
                            <i
                                class="fa-solid fa-bookmark"
                                aria-hidden="true"
                            />
                        </div>
                    {/if}
                </Tab>

                <Tab
                    {currentTab}
                    displayTab="Communication"
                    tabUrl="communication"
                    clickTabFunc={() => _checkItemsNotSaved("communication")}
                >
                    {#if patientEncounter.disableNotifications}
                        <div
                            class="tooltip tooltip-error ml-3"
                            data-tip="Communication is disabled for this encounter"
                        >
                            <i
                                class="fa fa-exclamation-circle"
                                aria-hidden="true"
                            />
                        </div>
                    {/if}
                </Tab>

                <Tab
                    {currentTab}
                    displayTab="Logs"
                    tabUrl="logs"
                    clickTabFunc={() => _checkItemsNotSaved("logs")}
                />
            </div>

            <div class="flex items-center flex-wrap gap-y-2 justify-end gap-x-3">
                {#if $isMobile}
                    <CommunicationToggle
                        {patientEncounter}
                        on:disableNotificationsToggled={() =>
                            (patientEncounter.disableNotifications =
                                !patientEncounter.disableNotifications)}
                    />
                    {#if currentTab == "generalInfo"}
                        <button
                            class={`${headerRightBtnCls} w-24`}
                            on:click={editEncounter}
                        >
                            <i class={`${editIcon}`} />
                            Edit
                        </button>
                    {/if}
                {/if}
                {#if !$isMobile}
                    {#if patientEncounter.isDeleted}
                        <p class="text-error font-bold pt-1">
                            Archived on {getFormatDate(patientEncounter.deletedDate)}
                        </p>
                    {/if}

                    {#if !isLoadingPatientEncounter}
                        {#if isUserEnableUpdatePatientEncounterStatus}
                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                            <div class="flex-none" on:mouseleave={_hideStatusMenu}>
                                <div class="menu menu-horizontal">
                                    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
                                    <li tabindex="0" on:mousemove={_showStatusMenu}>
                                        <div
                                            class={`${headerRightBtnCls} ${_getStatusBackgroundColor(patientEncounter.status)} flex items-center ${isChangingStatusOptionsOpen ? "rounded-t-lg" : "rounded-lg"}`}
                                        >
                                            {#if isUpdatingEncounterStatus}
                                                <Loader size="1rem" />
                                            {/if}
                                            <p>
                                                {getEncounterStatus(
                                                    patientEncounter.status,
                                                )}
                                            </p>
                                            <svg
                                                class="fill-current"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                ><path
                                                    d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                                                /></svg
                                            >
                                        </div>

                                        <ul
                                            id="encounterStatusDropDownMenu"
                                            class="z-[1000] dropdown-content menu border-0 bg-zinc-300 w-48 rounded-b-lg overflow-hidden"
                                        >
                                            <div>
                                                {#each encounterStatus as { id, type }}
                                                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                                                    <div
                                                        class={`flex items-center justify-start p-4 gap-x-2 border-0 h-12 cursor-pointer ${id == patientEncounter.status ? "bg-secondary text-white" : "bg-zinc-200 text-[#6f726c] hover:bg-zinc-300"}`}
                                                        on:click|preventDefault={() =>
                                                            _openConfirmUpdateEncounterStatusModal(
                                                                id,
                                                            )}
                                                    >
                                                        {type}
                                                    </div>
                                                {/each}
                                            </div>
                                        </ul>
                                    </li>
                                </div>
                            </div>
                        {/if}

                        <button
                            class={headerRightBtnCls}
                            on:click={showOcrStickerModal}
                        >
                            <i class="fa-solid fa-image" />
                            Sticker
                        </button>

                        {#if encounterEnableSaveAsPreset}
                            <button
                                class={headerRightBtnCls}
                                on:click={showSavePresetModal}
                            >
                                <i class="fa-solid fa-notes-medical" />
                                Save as Preset
                            </button>
                        {/if}

                        <button class={headerRightBtnCls} on:click={duplicateEncounter}>
                            <i class="fa fa-clone" />
                            Duplicate
                        </button>

                        {#if !patientEncounter.quoteOnly}
                            <button class={headerRightBtnCls} on:click={openEncounterListModal}>
                                <i class="fa fa-list" />
                                See List
                            </button>
                        {/if}

                        {#if !patientEncounter.isDeleted}
                            <button
                                class={`${headerRightBtnCls} w-24`}
                                on:click={() => {
                                    if (!isFromCalendarList)
                                        goToURL(
                                            `/${isPatientQuote ? "PatientQuote" : "PatientEncounter"}/edit/${patientEncounter.id}`,
                                        );
                                    else $EncounterSidePanelProps.type = "edit";
                                }}
                            >
                                <i class={`${editIcon}`} />
                                Edit
                            </button>
                        {/if}
                    {/if}
                {/if}
            </div>
        </div>

        <div class="flex flex-1 flex-col mx-4 mb-4">
            {#if currentTab == "generalInfo"}
                <PatientEncounterGeneralInfoTab
                    bind:patientEncounter
                    bind:isNotesInEdit
                    bind:notesRef
                    bind:patientDetails
                    bind:logsLoaded
                    bind:eclipseLogsData
                    {patientContacts}
                    {procedureDetails}
                    {doctorDetails}
                    billingData={patientEncounter.billingData}
                    encounterId={patientEncounter.id}
                    disableChangeProviderNo={encounterHaveQuotes ||
                        encounterHaveInvoices}
                    {doctorProviders}
                    {isEncounterEnableToEditPage}
                    {isFromCalendarList}
                    {reloadPatientDetails}
                    {reloadDoctorDetails}
                    {reloadProcedureDetails}
                    {updatePatientEncounter}
                    {loadEncounterStatusOfPatient}
                    reloadPatientEncounter={fullEncounterReload}
                />
            {:else if currentTab == "preOpQuestionnaire"}
                <PatientEncounterPreopQuestionnaireTab
                    encounterId={patientEncounter.id}
                    bind:questionnaireAnswer
                    bind:hasQuestionnaireAnswerLoaded
                    {patientEncounter}
                    {emailNotifications}
                    {smsNotifications}
                    {commsLoaded}
                    {reloadPatientEncounter}
                    {loadEncounterStatusOfPatient}
                    {loadCommunicationData}
                />
                <!-- {:else if currentTab == "postOpSurvey"}
                <PatientEncounterPostopSurveyTab
                    encounterId={patientEncounter.id}
                /> -->
            {:else if currentTab == "billing"}
                <PatientEncounterBillingTab
                    bind:patientEncounterDetails={patientEncounter}
                    {isUpdatingBillingStatus}
                    {billingDetails}
                    {latestQuoteData}
                    {latestQuoteDataItems}
                    {latestInvoiceData}
                    {latestInvoiceDataItems}
                    {quotesData}
                    {invoicesData}
                    {paymentsData}
                    {doctorProviders}
                    {isFromCalendarList}
                    bind:itemsNotSaved
                    bind:logsLoaded
                    bind:doctorPresets
                    bind:hasDoctorPresetsFetched
                    bind:doctorBillingRules
                    bind:hasDoctorBillingRulesFetched
                    {reloadBillingDetails}
                    reloadPatientEncounter={fullEncounterReload}
                />
            {:else if currentTab == "notes"}
                <PatientEncounterNotesTab
                    patientID={patientDetails.id}
                    patientHasNotes={patientDetails.hasNotes}
                    encounterID={patientEncounter.id}
                    encounterHasNotes={patientEncounter.hasNotes}
                    {reloadPatientDetails}
                    encounterDoctorGroupId={patientEncounter.doctor?.groupId}
                />
            {:else if currentTab == "communication"}
                <PatientEncounterCommunicationTab
                    bind:patientEncounterDetails={patientEncounter}
                    {patientDetails}
                    {doctorDetails}
                    {emailNotifications}
                    {smsNotifications}
                    bind:isLoading={isCommsLoading}
                    {reloadCommunication}
                />
            {:else if currentTab == "logs"}
                <PatientEncounterLogsTab
                    bind:logsLoaded
                    bind:eclipseLogsData
                    bind:activityLogsData
                    bind:totalActivityLogs
                    bind:totalEclipseLogs
                    bind:extendedLogs
                    patientEncounterId={patientEncounter.id}
                />
            {/if}
            <div class="flex flex-row gap-x-2 pt-2 justify-start items-center">
                {#if ocrText}
                    <div class="p-1 pl-2 pr-2 text-xs bg-gray-100 rounded">{ocrText}</div>
                {/if}
                <div class="pr-2 border-r border-r-gray-300 text-xs"> 
                    Encounter created on: <b>{_getDisplayDate(patientEncounter.createdDate)}</b>
                </div>
                <div class="text-xs">
                    Last updated on: <b>{_getDisplayDate(patientEncounter.updatedDate)}</b>
                </div>
                <div class="flex-1 flex justify-end pr-4">
                    <CustomTooltip
                        contentClass="bg-[#fff] border-none"
                        contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                    >
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <div slot="tooltipTrigger" class="text-xs flex gap-1 cursor-pointer hover:underline" 
                            on:click={async () => {
                                await reloadBillingDetails(true, null);
                            }}>
                            <p>Reload <i class="fa-solid fa-refresh"></i></p> 
                        </div>
                        

                        Reload encounter details
                    </CustomTooltip>
                </div>
            </div>
        </div>
    </div>
{/if}


<OcrStickerModal
    bind:isModalOpen={isOcrStickerModalOpen}
    encounterId={patientEncounter.id}
    accountId={encounterDoctorId}
/>

<NotificationModal
    isModalOpen={isSuccessSavePresetModalOpen}
    closeModal={() => closeSavePresetModal()}
    closeModalFunction={() => closeSavePresetModal()}
    showConfirmBtn={false}
    title="done"
>
    <div class="flex gap-x-4 items-center leading-6">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            class="stroke-current flex-shrink-0 h-6 w-6 text-secondary"
            fill="none"
            viewBox="0 0 24 24"
            ><path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            /></svg
        >
        <p>Preset successfully created.</p>
    </div>
</NotificationModal>

{#if isSavePresetModalOpen}
    <CreatePresetModal {patientEncounter} closeModal={closeSavePresetModal} />
{/if}

<NotificationModal
    isModalOpen={isConfirmUpdateEncounterStatusModalOpen}
    title="Update Encounter Status"
    cancelText="Cancel"
    submitText="Confirm"
    confirmButtonClass={newStatus == DirectEncounterStatus.Archived
        ? "btn-error"
        : "btn-secondary"}
    cancelButtonClass="btn-accent"
    closeModal={_closeConfirmUpdateEncounterStatusModal}
    confirmAction={handleUpdateStatus}
    isConfirming={isUpdatingEncounterStatus}
    closeModalFunction={_closeConfirmUpdateEncounterStatusModal}
>
    {#if encounterHasActiveClaim && checkClaimStatusViaChangeStatus.includes(newStatus)}
        <div class="flex flex-col gap-y-2">
            <p>
                Encounter has an active claim. Archiving or cancelling the
                encounter will cancel this active claim.
            </p>
            <p>Do you still wish to proceed?</p>
        </div>
    {:else}
        Change encounter status to {getEncounterStatus(newStatus)}?
    {/if}
</NotificationModal>

<NotificationModal
    isModalOpen={isConfirmCancelEncounterWithPatientPaidPaymentsModalOpen}
    title="Update Encounter Status"
    modalMaxWidth="max-w-xl"
    showCancelBtn={false}
    showConfirmBtn={false}
    closeModalFunction={_closeConfirmUpdateEncounterStatusModal}
    isConfirming={isUpdatingEncounterStatus}
    btnOptions={[
        {
            text: "Cancel",
            class: "btn-accent w-auto",
            loading: isUpdatingEncounterStatus,
            function: () => _closeConfirmUpdateEncounterStatusModal(),
        },
        {
            text: isUpdatingEncounterStatus
                ? "Confirming"
                : "Confirm, No Refund",
            class: "btn-secondary text-xs w-auto",
            loading: isUpdatingEncounterStatus,
            function: () => handleUpdateStatus(true),
        },
        {
            text: isUpdatingEncounterStatus
                ? "Confirming"
                : "Confirm and Refund",
            class: "btn-secondary text-xs w-auto",
            loading: isUpdatingEncounterStatus,
            function: () => handleUpdateStatus(),
        },
    ]}
>
    Change encounter status to Cancelled and refund patient?
</NotificationModal>

<ViewEncounterListModal
    bind:isModalOpen={isEncounterListModalOpen}
    encounterId={patientEncounter.id}
    {encounterDoctorId}
/>
